import React from "react";
import { Library, Monitor, School, FlaskConical, Trophy, Bus, Wifi, ShieldCheck, HeartPulse, Utensils, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { facilities } from "../mock/mock";
import { PageHero } from "./About";

const iconMap = { Library, Monitor, School, FlaskConical, Trophy, Bus };

const Facilities = () => {
  const extras = [
    { icon: Wifi, title: "Campus Wi-Fi", text: "High-speed Wi-Fi across the campus for uninterrupted learning." },
    { icon: ShieldCheck, title: "24x7 Security", text: "CCTV surveillance and trained personnel ensuring a safe environment." },
    { icon: HeartPulse, title: "Medical Room", text: "Basic first-aid, medical support and tie-up with local hospitals." },
    { icon: Utensils, title: "Cafeteria", text: "Hygienic canteen offering affordable meals and refreshments." },
  ];

  return (
    <div>
      <PageHero
        eyebrow="Facilities"
        title="World-class infrastructure"
        subtitle="An environment designed to nurture curiosity, creativity, and character."
        image="https://images.unsplash.com/photo-1501503069356-3c6b82a17d89"
      />

      <section className="section-y bg-white">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {facilities.map((f, i) => {
            const Icon = iconMap[f.icon] || School;
            return (
              <article
                key={i}
                className="group relative rounded-sm overflow-hidden bg-brand-ivory border border-brand-gold/25 hover-lift animate-fade-up"
                style={{ animationDelay: `${i * 0.08}s` }}
              >
                <div className="h-56 overflow-hidden">
                  <img
                    src={f.image}
                    alt={f.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                </div>
                <div className="p-7">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-11 h-11 rounded-full bg-brand-green flex items-center justify-center border border-brand-gold">
                      <Icon className="w-5 h-5 text-brand-gold" />
                    </div>
                    <h3 className="font-serif-display text-2xl text-brand-green-deep">{f.title}</h3>
                  </div>
                  <p className="text-neutral-600 text-sm leading-relaxed">{f.desc}</p>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="section-y bg-brand-ivory">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-12">
            <div className="divider-ornament text-xs uppercase tracking-[0.3em] mb-3">Beyond classrooms</div>
            <h2 className="font-serif-display text-4xl md:text-5xl font-bold text-brand-green-deep">More that makes us home</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
            {extras.map((e, i) => (
              <div key={i} className="bg-white rounded-sm p-6 border border-brand-gold/25 hover-lift animate-fade-up" style={{ animationDelay: `${i * 0.08}s` }}>
                <e.icon className="w-8 h-8 text-brand-gold-dark mb-4" />
                <h4 className="font-serif-display text-lg text-brand-green-deep mb-2">{e.title}</h4>
                <p className="text-sm text-neutral-600 leading-relaxed">{e.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 bg-brand-green-deep text-brand-ivory">
        <div className="max-w-5xl mx-auto px-6 text-center">
          <h3 className="font-serif-display text-3xl md:text-4xl font-bold mb-4">
            See it in <span className="text-brand-gold italic">person.</span>
          </h3>
          <p className="text-brand-ivory/80 mb-8">Schedule a campus visit and experience our facilities first-hand.</p>
          <Link to="/contact" className="inline-flex items-center gap-2 btn-gold px-7 py-3 rounded-full font-semibold">
            Book a Campus Tour <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Facilities;
